import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import { 
  Activity, Clock, MapPin, CheckCircle, AlertCircle, X, RefreshCw,
  Play, Pause, Square, MessageSquare, Flag, Calendar, User, TrendingUp,
  Eye, ChevronRight, Sparkles, FileText, Camera, Send, AlertTriangle
} from 'lucide-react';

export default function ProjectTracker({ job, onClose, onStatusChange }) {
  const [loading, setLoading] = useState(true);
  const [jobDetails, setJobDetails] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [showEscalation, setShowEscalation] = useState(false);
  const [escalationNote, setEscalationNote] = useState('');

  useEffect(() => {
    if (job) {
      loadJobDetails();
      loadTimeline();
      loadAttendance();
    }
  }, [job]);

  const loadJobDetails = async () => {
    setLoading(true);
    try {
      const response = await apiCall(`/api/employer/jobs/${job._id}/details`);
      setJobDetails(response.job);
    } catch (error) {
      console.error('Failed to load job details:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTimeline = async () => {
    try {
      const response = await apiCall(`/api/employer/jobs/${job._id}/timeline`);
      setTimeline(response.events || []);
    } catch (error) {
      console.error('Failed to load timeline:', error);
    }
  };

  const loadAttendance = async () => {
    try {
      const response = await apiCall(`/api/employer/jobs/${job._id}/attendance`);
      setAttendance(response.records || []);
    } catch (error) {
      console.error('Failed to load attendance:', error);
    }
  };

  const loadAIInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await apiCall(`/api/employer/jobs/${job._id}/ai-insights`);
      setAiInsights(response.insights);
    } catch (error) {
      toast.error('Failed to load AI insights');
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleEscalate = async () => {
    if (!escalationNote.trim()) {
      toast.error('Please describe the issue');
      return;
    }

    try {
      await apiCall(`/api/employer/jobs/${job._id}/escalate`, {
        method: 'POST',
        body: JSON.stringify({ note: escalationNote })
      });
      toast.success('Issue escalated to support team');
      setShowEscalation(false);
      setEscalationNote('');
      loadTimeline();
    } catch (error) {
      toast.error(error.message || 'Failed to escalate');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-blue-100 text-blue-700 border-blue-200',
      assigned: 'bg-amber-100 text-amber-700 border-amber-200',
      in_progress: 'bg-violet-100 text-violet-700 border-violet-200',
      pending_approval: 'bg-orange-100 text-orange-700 border-orange-200',
      completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[status] || colors.active;
  };

  const getTimelineIcon = (eventType) => {
    const icons = {
      job_created: <FileText className="w-4 h-4" />,
      worker_assigned: <User className="w-4 h-4" />,
      check_in: <Play className="w-4 h-4" />,
      check_out: <Square className="w-4 h-4" />,
      break_start: <Pause className="w-4 h-4" />,
      break_end: <Play className="w-4 h-4" />,
      message_sent: <MessageSquare className="w-4 h-4" />,
      escalation: <Flag className="w-4 h-4" />,
      status_change: <Activity className="w-4 h-4" />,
      completion_submitted: <CheckCircle className="w-4 h-4" />
    };
    return icons[eventType] || <Activity className="w-4 h-4" />;
  };

  const calculateProgress = () => {
    if (!jobDetails) return 0;
    const totalMinutes = jobDetails.duration_minutes || 480; // 8 hours default
    const workedMinutes = attendance.reduce((sum, a) => sum + (a.minutes_worked || 0), 0);
    return Math.min(100, Math.round((workedMinutes / totalMinutes) * 100));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <Activity className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Project Tracker</h2>
                <p className="text-white/80">{job?.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(jobDetails?.status || job?.status)}`}>
                {(jobDetails?.status || job?.status)?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
              <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-white/80">Overall Progress</span>
              <span className="font-semibold">{calculateProgress()}%</span>
            </div>
            <div className="h-3 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full transition-all duration-500"
                style={{ width: `${calculateProgress()}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[65vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-violet-600 animate-spin" />
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {/* Left Column - Status & Workers */}
              <div className="space-y-6">
                {/* Worker Status */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-violet-600" />
                    Assigned Workers
                  </h3>
                  
                  {jobDetails?.assigned_workers?.length > 0 ? (
                    <div className="space-y-3">
                      {jobDetails.assigned_workers.map((worker, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                              <span className="text-sm font-semibold text-violet-600">
                                {worker.full_name?.charAt(0) || 'W'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{worker.full_name}</p>
                              <p className="text-xs text-gray-500">{worker.status || 'Assigned'}</p>
                            </div>
                          </div>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            worker.is_checked_in 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {worker.is_checked_in ? 'Working' : 'Not Started'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-lg">
                      <User className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No workers assigned yet</p>
                    </div>
                  )}
                </div>

                {/* Attendance Summary */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-violet-600" />
                    Attendance
                  </h3>
                  
                  {attendance.length > 0 ? (
                    <div className="space-y-3">
                      {attendance.map((record, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">{record.worker_name}</span>
                            <span className="text-sm text-gray-500">
                              {record.minutes_worked ? `${Math.floor(record.minutes_worked / 60)}h ${record.minutes_worked % 60}m` : '-'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            {record.check_in_time && (
                              <span className="flex items-center gap-1">
                                <Play className="w-3 h-3 text-emerald-500" />
                                {new Date(record.check_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                            {record.check_out_time && (
                              <span className="flex items-center gap-1">
                                <Square className="w-3 h-3 text-red-500" />
                                {new Date(record.check_out_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                            {record.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                Verified
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-gray-50 rounded-lg">
                      <Clock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No attendance records yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Middle Column - Timeline */}
              <div className="space-y-6">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-violet-600" />
                    Activity Timeline
                  </h3>
                  
                  {timeline.length > 0 ? (
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                      <div className="space-y-4">
                        {timeline.map((event, idx) => (
                          <div key={idx} className="relative flex items-start gap-4 pl-10">
                            <div className={`absolute left-2 w-5 h-5 rounded-full flex items-center justify-center ${
                              event.type === 'escalation' 
                                ? 'bg-red-100 text-red-600' 
                                : 'bg-violet-100 text-violet-600'
                            }`}>
                              {getTimelineIcon(event.type)}
                            </div>
                            <div className="flex-1 pb-4">
                              <p className="text-sm font-medium text-gray-900">{event.title}</p>
                              <p className="text-xs text-gray-500">{event.description}</p>
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(event.timestamp).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <Activity className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No activity yet</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - AI Insights & Actions */}
              <div className="space-y-6">
                {/* AI Insights */}
                <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl border border-violet-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-violet-900 flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      AI Insights
                    </h3>
                    <button
                      onClick={loadAIInsights}
                      disabled={loadingInsights}
                      className="px-3 py-1.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition disabled:opacity-50"
                    >
                      {loadingInsights ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Analyze'}
                    </button>
                  </div>
                  
                  {aiInsights ? (
                    <div className="space-y-3">
                      <div className="p-3 bg-white rounded-lg">
                        <p className="text-sm font-medium text-gray-900 mb-1">Progress Status</p>
                        <p className="text-sm text-gray-600">{aiInsights.progress_summary}</p>
                      </div>
                      {aiInsights.risks && aiInsights.risks.length > 0 && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm font-medium text-amber-900 mb-1 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            Potential Risks
                          </p>
                          <ul className="text-sm text-amber-700 list-disc list-inside">
                            {aiInsights.risks.map((risk, idx) => (
                              <li key={idx}>{risk}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {aiInsights.recommendations && (
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <p className="text-sm font-medium text-emerald-900 mb-1">Recommendations</p>
                          <p className="text-sm text-emerald-700">{aiInsights.recommendations}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-white/50 rounded-lg">
                      <Sparkles className="w-10 h-10 text-violet-300 mx-auto mb-2" />
                      <p className="text-violet-600 text-sm">Click "Analyze" for AI-powered insights</p>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
                  <div className="space-y-2">
                    <button className="w-full px-4 py-3 bg-gray-50 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                      Message Workers
                    </button>
                    <button 
                      onClick={() => setShowEscalation(true)}
                      className="w-full px-4 py-3 bg-red-50 text-red-700 rounded-lg font-medium hover:bg-red-100 transition flex items-center gap-3"
                    >
                      <Flag className="w-5 h-5" />
                      Report Issue
                    </button>
                    <button className="w-full px-4 py-3 bg-gray-50 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition flex items-center gap-3">
                      <Eye className="w-5 h-5 text-violet-600" />
                      View Full Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Escalation Modal */}
        {showEscalation && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Flag className="w-5 h-5 text-red-600" />
                  Report Issue
                </h3>
                <button onClick={() => setShowEscalation(false)} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <textarea
                value={escalationNote}
                onChange={(e) => setEscalationNote(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 resize-none"
                rows={4}
                placeholder="Describe the issue you're experiencing..."
              />
              <button
                onClick={handleEscalate}
                className="w-full mt-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                Submit Report
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
